import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationFeed = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'job_match',
      title: 'Nieuwe Opdracht Match',
      message: 'Warmtepomp installatie in Amsterdam komt overeen met uw vaardigheden',
      timestamp: '2026-01-14T20:30:00',
      read: false,
      priority: 'high',
      actionLink: '/contractor-job-browser'
    },
    {
      id: 2,
      type: 'payment',
      title: 'Betaling Ontvangen',
      message: '€850,00 voor "Airco installatie Rotterdam" is uitbetaald',
      timestamp: '2026-01-14T18:15:00',
      read: false,
      priority: 'high',
      actionLink: null
    },
    {
      id: 3,
      type: 'review',
      title: 'Nieuwe Beoordeling',
      message: 'Klant heeft 5 sterren gegeven voor uw laatste opdracht',
      timestamp: '2026-01-14T15:45:00',
      read: true,
      priority: 'medium',
      actionLink: '/user-profile-management'
    },
    {
      id: 4,
      type: 'message',
      title: 'Nieuw Bericht',
      message: 'Jan Bakker heeft een vraag over de warmtepomp installatie',
      timestamp: '2026-01-14T14:20:00',
      read: true,
      priority: 'medium',
      actionLink: null
    },
    {
      id: 5,
      type: 'reminder',
      title: 'Opdracht Herinnering',
      message: 'Zonnepanelen installatie start morgen om 09:00',
      timestamp: '2026-01-14T12:00:00',
      read: true,
      priority: 'low',
      actionLink: '/job-details-and-applications'
    }
  ]);

  const getNotificationIcon = (type) => {
    const iconMap = {
      job_match: 'Briefcase',
      payment: 'CreditCard',
      review: 'Star',
      message: 'MessageSquare',
      reminder: 'Bell'
    };
    return iconMap?.[type] || 'Bell';
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      job_match: 'text-primary',
      payment: 'text-success',
      review: 'text-accent',
      message: 'text-secondary',
      reminder: 'text-warning'
    };
    return colorMap?.[type] || 'text-muted-foreground';
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return (
        <span className="px-2 py-0.5 bg-error/10 text-error text-xs font-medium rounded">
          Belangrijk
        </span>
      );
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) {
      return `${diffMins} min geleden`;
    } else if (diffHours < 24) {
      return `${diffHours} uur geleden`;
    } else {
      return date?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    }
  };

  const markAsRead = (id) => {
    setNotifications(notifications?.map(n =>
      n?.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications?.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-6"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center relative">
            <Icon name="Bell" size={20} color="var(--color-primary)" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs font-medium rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              Meldingen
            </h3>
            <p className="caption text-muted-foreground">
              {unreadCount} ongelezen {unreadCount === 1 ? 'melding' : 'meldingen'}
            </p>
          </div>
        </div>

        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={markAllAsRead}>
            Alles markeren
          </Button>
        )}
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {notifications?.map((notification) => (
          <div
            key={notification?.id}
            onClick={() => markAsRead(notification?.id)}
            className={`
              bg-muted/50 rounded-lg p-3 md:p-4 border border-border cursor-pointer
              transition-smooth hover:border-primary/30
              ${!notification?.read ? 'bg-primary/5 border-primary/20' : ''}
            `}
          >
            <div className="flex gap-3">
              <div className={`flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                <Icon name={getNotificationIcon(notification?.type)} size={20} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-medium text-sm text-foreground line-clamp-1">
                    {notification?.title}
                  </h4>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getPriorityBadge(notification?.priority)}
                    {!notification?.read && (
                      <span className="w-2 h-2 bg-primary rounded-full" />
                    )}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                  {notification?.message}
                </p>

                <div className="flex items-center justify-between">
                  <span className="caption text-muted-foreground">
                    {formatTimestamp(notification?.timestamp)}
                  </span>

                  {notification?.actionLink && (
                    <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                      Bekijken
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <Button variant="outline" fullWidth iconName="Eye" iconPosition="left">
          Alle Meldingen Bekijken
        </Button>
      </div>
    </div>
  );
};

export default NotificationFeed;